import {useState, memo, ChangeEvent} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {useToggle} from '~/shared/hooks';
import {media} from '~/shared/theme/utils';
import {attachModal} from '~/shared/components/ReduxModal';
import {PopupModalBase} from '~/shared/components/Modals';
import {createLogger} from '~/shared/logging';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {bodyRegular, headingS, headingXS, smallRegular} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import FormPieInput from '~/shared/components/FormField/FormInput/FormPieInput';

import {PieCloseModalButton, PieModalHeader} from '../../styled';

import bagFeesImgSrc from './bagFeesImg.png';

const logger = createLogger('CheckoutErrorMessageModal');

const Root = styled(PopupModalBase)`
  background-color: ${({theme}) => theme.colors.surface};

  &&& {
    background-color: ${({theme}) => theme.colors.surface};
    width: 100%;
    height: auto;
  }

  ${media.minLargeMobile`
    &&& {
      width: 500px;
    }
  `}
`;

const ContentContainer = styled.div`
  padding: 32px 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${({theme}) => theme.colors.contentDefault};
`;

const Title = styled.h2`
  ${headingS}
  margin-top: 24px;
`;

const Subtitle = styled.span`
  ${bodyRegular}
  margin-top: 8px;
`;

const Image = styled(ImageWithAlt).attrs({
  src: bagFeesImgSrc,
  alt: 'Checkout error image',
})`
  width: 200px;
  height: 200px;
`;

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row-reverse;
  gap: 16px;
  margin-top: 32px;
`;

const ActionButton = styled(PieButton)`
  flex: 1;
`;

const ServiceCodeViewHeader = styled.h3`
  ${headingXS}
  padding: 16px;
  color: ${({theme}) => theme.colors.contentDefault};
  width: 100%;
  text-align: start;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  ${media.minLargeMobile`
    text-align: center;
  `}
`;

const ErrorMessage = styled.span`
  ${smallRegular}
  display: block;
  width: 100%;
  color: ${({theme}) => theme.colors.contentError};
  height: 20px;
  margin-top: 8px;
`;

type ModalArgs = {
  title: string;
  subtitle?: string;
  showServiceRepresentativeLink: boolean;
  onSubmit?: (code: string, onError: () => void) => Promise<void>;
};

const MainContent = ({title, subtitle, showServiceRepresentativeLink, closeModal, onCodeClick}: {
  title: ModalArgs['title'];
  subtitle: ModalArgs['subtitle'];
  showServiceRepresentativeLink: ModalArgs['showServiceRepresentativeLink'];
  closeModal: () => void;
  onCodeClick: () => void;
}) => {
  const {t} = getLocalizationService();

  return (
    <>
      <PieModalHeader />
      <ContentContainer id="modal-content">
        <Image />
        <Title id="modal-title">{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        <ActionsContainer>
          <ActionButton variant="primaryXL" onClick={closeModal}>
            {t('accept')}
          </ActionButton>
          {showServiceRepresentativeLink && (
            <ActionButton variant="secondaryXL" onClick={onCodeClick}>
              {t('i_have_a_code_from_customer_service')}
            </ActionButton>
          )}
        </ActionsContainer>
      </ContentContainer>
    </>
  );
};

const ServiceCodeInputContent = ({onSubmit}: {
  onSubmit: ModalArgs['onSubmit'];
}) => {
  const [code, setCode] = useState('');
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {t} = getLocalizationService();

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  };

  const onSubmitHandler = async () => {
    if (!code || !onSubmit) {
      logger.warn('the code is empty or the submit handler wasnt passed');
      return;
    }

    setShowError(false);
    try {
      setIsLoading(true);
      await onSubmit(code, () => setShowError(true));
    } catch (error) {
      logger.error('error while submitting the code', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ServiceCodeViewHeader>{t('i_have_a_service_code')}</ServiceCodeViewHeader>
      <ContentContainer id="modal-content">
        <FormPieInput
          placeholder={t('enter_your_code_here')}
          hasError={showError}
          value={code}
          onChange={onChangeHandler}
          aria-autocomplete="none"
          aria-label="service code input"
          data-test-id="service-code-input"
        />
        {showError && <ErrorMessage>{t('this_code_didnt_work_please_double_check')}</ErrorMessage>}
        <ActionsContainer>
          <ActionButton isLoading={isLoading} variant="primaryXL" onClick={onSubmitHandler}>{t('accept')}</ActionButton>
        </ActionsContainer>
      </ContentContainer>
    </>
  );
};

type CheckoutErrorMessageModalProps = {
  args: ModalArgs;
  closeModal: () => void;
};

const CheckoutErrorMessageWithCodeModal = ({
  args,
  closeModal,
}: CheckoutErrorMessageModalProps) => {
  const [shouldShowServiceCodeInput, toggle] = useToggle(false);

  return (
    <Root>
      <PieCloseModalButton onClick={closeModal} hideOnMobile />
      {shouldShowServiceCodeInput ? (
        <ServiceCodeInputContent onSubmit={args.onSubmit} />
      ) : (
        <MainContent
          title={args.title}
          subtitle={args.subtitle}
          showServiceRepresentativeLink={args.showServiceRepresentativeLink}
          closeModal={closeModal}
          onCodeClick={toggle}
        />
      )}
    </Root>
  );
};

CheckoutErrorMessageWithCodeModal.hideCloseButton = true;
CheckoutErrorMessageWithCodeModal.mobileBottomSheet = true;
attachModal('checkout_error_message_with_code_modal', CheckoutErrorMessageWithCodeModal);

export default memo(CheckoutErrorMessageWithCodeModal);
